import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Send } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';

interface Notification {
  id: string;
  message: string;
  created_at: string;
  is_from_admin: boolean;
  is_read: boolean;
  user_id: string | null;
  admin_id: string | null;
}

export const NotificationList = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchNotifications();
    
    // Set up real-time subscription
    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;

    try {
      console.log('Fetching notifications for user:', user.id);
      
      // Updated query to fetch both sent and received messages
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .or(`user_id.eq.${user.id},and(is_from_admin.eq.true,user_id.eq.${user.id}),admin_id.eq.${user.id}`)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching notifications:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch notifications"
        });
        return;
      }

      console.log('Fetched notifications:', data);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch notifications"
      });
    }
  };

  const sendMessage = async () => {
    if (!user || !newMessage.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a message"
      });
      return;
    }

    const notificationData = {
      message: newMessage,
      user_id: user.id,
      is_from_admin: false,
      admin_id: null // This ensures the message goes to admin
    };

    console.log('Sending notification:', notificationData);

    const { error } = await supabase
      .from('notifications')
      .insert([notificationData]);

    if (error) {
      console.error('Error sending message:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send message"
      });
      return;
    }

    setNewMessage('');
    toast({
      title: "Success",
      description: "Message sent successfully"
    });
  };

  const renderMessage = (notification: Notification) => {
    const isFromMe = !notification.is_from_admin && notification.user_id === user?.id;

    return (
      <div
        key={notification.id}
        className={`flex ${isFromMe ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div
          className={`max-w-[70%] rounded-lg p-3 ${
            isFromMe
              ? 'bg-blue-600 text-white ml-auto'
              : 'bg-gray-700 text-white'
          }`}
        >
          <p className="text-sm">{notification.message}</p>
          <p className="text-xs mt-1 opacity-70">
            {format(new Date(notification.created_at), 'PPpp')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-[#2A2F3C] border-gray-600">
        <h3 className="text-lg font-semibold text-white mb-4">Chat with Admin</h3>
        <ScrollArea className="h-[400px] pr-4 mb-4">
          <div className="space-y-2">
            {notifications.map(renderMessage)}
          </div>
        </ScrollArea>

        <div className="flex gap-4">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 rounded-lg bg-[#2A2F3C] text-white border border-gray-600 focus:outline-none focus:border-blue-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                sendMessage();
              }
            }}
          />
          <Button onClick={sendMessage} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
            <Send className="w-4 h-4" />
            Send
          </Button>
        </div>
      </Card>
    </div>
  );
};